import { Text } from 'src/design/atoms/Text';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { JobChangeOrderDto } from 'src/api/FiretrolApiClient';
import { formatCurrency } from 'src/design/atoms/Currency';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { formatDate } from 'src/utils';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface ChangeOrderDialogProps {
  handleClose: () => void;
  open: boolean;
  changeOrders: JobChangeOrderDto[];
}

export const ChangeOrderDialog: React.FC<ChangeOrderDialogProps> = ({
  handleClose,
  open,
  changeOrders,
}) => {
  const columns = React.useMemo<MRT_ColumnDef<JobChangeOrderDto>[]>(
    () => [
      {
        accessorKey: 'changeOrderNbr',
        header: 'Change Order Num',
        size: 150,
      },
      {
        accessorKey: 'changeOrderStatus',
        header: 'Status',
        size: 120,
      },
      {
        accessorKey: 'trimmedChgOrderDescription',
        header: 'Description',
        size: 250,
      },
      {
        id: 'changeOrderEstCost',
        accessorFn: (row) =>
          formatCurrency(row.changeOrderEstCost || 0, { precise: true }),
        header: 'Est Cost',
        size: 150,
      },
      {
        accessorKey: 'postingStatus',
        header: 'Posting Status',
        size: 150,
      },
      {
        id: 'postingDate',
        accessorFn: (row) => formatDate(row.postingDate),
        header: 'Posting Date',
        size: 150,
      },
      {
        id: 'lastUpdated',
        accessorFn: (row) => formatDate(row.lastUpdated),
        header: 'Last Updated',
        size: 150,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'ctc-change-orders-dialog-table',
    columns: columns,
    defaultSorting: [{ id: 'postingDate', desc: true }],
  });

  const tableExports = useTableExports<JobChangeOrderDto>(
    'ctc-change-orders',
    (data) => {
      return data.map((item) => ({
        ['Change Order Num']: item.changeOrderNbr,
        ['Status']: item.changeOrderStatus,
        ['Description']: item.trimmedChgOrderDescription,
        ['Est Cost']: item.changeOrderEstCost || 0,
        ['Posting Status']: item.postingStatus,
        ['Posting Date']: formatDate(item.postingDate),
        ['Last Updated']: formatDate(item.lastUpdated),
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: changeOrders,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.changeOrderNbr),
    totalCount: changeOrders.length,
    isLoading: false,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <BaseModal
      opened={open}
      onClose={handleClose}
      title="Change Orders"
      size="xl"
    >
      {changeOrders.length > 0 ? (
        <>{table}</>
      ) : (
        <Text c="dimmed" ta="center" py="xl">
          No change orders found
        </Text>
      )}
    </BaseModal>
  );
};
